import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, KeyVal, Input } from 'equasion-lib';
import ZonePickerModal, { useZoneScanOnLoad } from '@/lib/shared-coms/ZonePickerModal';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import { FiCheckCircle, FiEdit } from 'react-icons/fi';
import ProductInLocation from '@/lib/shared-coms/ProductInLocation';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import { useRootStore } from '@/lib/providers/RootProvider';
import './DestroyUnserialized.scss';

// Each destroyable line is one (balance row, bucket) pair so a single lot that has
// both stocked and quarantined units can be disposed of from either bucket.
const lineKey = (id, fromStatus) => `${id}:${fromStatus}`;

const DestroyUnserialized = ({
  destroyUnserializedItems,
  getZoneInventoryBalances,
  zoneInventoryBalances = []
}) => {
  const companyId = useRootStore((state) => state.selectedCompany.id);
  const [destroyAmounts, setDestroyAmounts] = useState({});
  const [destructionReason, setDestructionReason] = useState('');
  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const navigate = useNavigate();
  const [destroyed, setDestroyed] = useState(false);
  const [zoneId, setZoneId] = useState(null);
  const [zoneName, setZoneName] = useState('');
  // The zone is the only thing to give before the items, so its scanner opens with the screen.
  const { tapPrompt, pickerProps } = useZoneScanOnLoad(
    () => setZoneModalOpen(true),
    !zoneId
  );

  // Flatten balances into destroyable lines (one per non-empty bucket).
  const destroyLines = zoneInventoryBalances.flatMap((balance) => {
    const { id, lot, stocked, quarantined, product } = balance;
    return [
      stocked > 0 && { id, lot, product, fromStatus: 'STOCKED', available: stocked },
      quarantined > 0 && {
        id,
        lot,
        product,
        fromStatus: 'QUARANTINED',
        available: quarantined
      }
    ].filter(Boolean);
  });

  useEffect(() => {
    setDestroyAmounts({});
  }, [zoneInventoryBalances]);

  const submitDestroyUnserializedItems = async () => {
    try {
      await destroyUnserializedItems({
        variables: {
          companyId,
          destructionReason,
          destroyUnserializedItemsAttributes: destroyLines
            .map(({ id, fromStatus }) => ({
              zoneInventoryBalanceId: id,
              fromStatus,
              quantity: destroyAmounts[lineKey(id, fromStatus)] || 0
            }))
            .filter(({ quantity }) => quantity > 0)
        }
      });
      setDestroyed(true);
    } catch (e) {
      console.log(`ERROR DESTROYING PRODUCTS: ${e}`);
    }
  };

  const hasAnyQuantity = Object.values(destroyAmounts).some((quantity) => quantity > 0);

  return (
    <Page className="destroy destroy-unserialized action-page">
      <ActionTopBar action="Destroy Unserialized" backPath="/" actionColor="#C42525" />
      <div className="action-page-body">
        <div
          className={['stock-zone', ...(!zoneId ? ['active'] : [])].join(' ')}
          onClick={() => setZoneModalOpen(true)}
        >
          <FiEdit />
          <h1>Zone</h1>
          <h2 className={!zoneName ? 'zone-placeholder' : ''}>{zoneName || tapPrompt}</h2>
        </div>
        {!!zoneId && !destroyLines.length && <div className="empty-zone">Empty zone</div>}
        {destroyLines.map(({ id, lot, product, fromStatus, available }) => {
          const key = lineKey(id, fromStatus);
          return (
            <ProductInLocation
              key={key}
              product={product}
              lot={lot}
              quantity={available}
              actionType={`Destroy (${
                fromStatus === 'QUARANTINED' ? 'Quarantined' : 'Stocked'
              })`}
              newQuantity={destroyAmounts[key] ?? 0}
              setNewQuantity={(value) =>
                setDestroyAmounts((amounts) => ({
                  ...amounts,
                  [key]: value ? Math.min(parseInt(value), available) : parseInt(value)
                }))
              }
            />
          );
        })}
        {!!destroyLines.length && (
          <Input
            form={false}
            inputType="textarea"
            value={destructionReason}
            onChange={({ value }) => setDestructionReason(value)}
            label="Destruction Reason"
          />
        )}
        <Button
          type="danger"
          fullWidth
          className="destroy-unserialized__submit"
          onClick={submitDestroyUnserializedItems}
          disabled={!zoneId || !hasAnyQuantity || !destructionReason.trim()}
        >
          Destroy Unserialized
        </Button>
      </div>
      {destroyed && (
        <div className="result-panel open">
          <div className="result-message">
            <FiCheckCircle style={{ color: 'var(--green)' }} />
            <h1>Destroyed Unserialized Successfully</h1>
            <p>
              The following items have been destroyed from zone <b>{zoneName}</b>
            </p>

            {destroyLines
              .map(({ id, fromStatus, product, lot }) => ({
                product,
                lot,
                fromStatus,
                quantity: destroyAmounts[lineKey(id, fromStatus)] || 0
              }))
              .filter(({ quantity }) => quantity > 0)
              .map(({ product, lot, fromStatus, quantity }, i) => (
                <div className="destroyed-products" key={i}>
                  <WarehouseProductDisplay product={product} />
                  <KeyVal
                    items={[
                      { key: 'Lot', value: lot },
                      {
                        key: 'From',
                        value:
                          fromStatus === 'QUARANTINED' ? 'Quarantined' : 'Stocked'
                      },
                      { key: 'Quantity', value: quantity }
                    ]}
                  />
                </div>
              ))}
            <b>Reason for destruction</b>
            <br />
            {destructionReason}
          </div>
          <div className="result-actions">
            <Button type="primary" fullWidth onClick={() => navigate('/')}>
              Done
            </Button>
          </div>
        </div>
      )}
      <ZonePickerModal
        className="stock-zone-modal"
        open={zoneModalOpen}
        {...pickerProps(() => setZoneModalOpen(false))}
        title="Select zone:"
        companyId={companyId}
        // Stocked and quarantined units are both destroyable, so neither zone type is excluded.
        scanTitle="Scan the zone to destroy from"
        scanHint="Destroy unserialized: scan the label on the zone holding the items being destroyed. You choose the items and quantities next."
        onZoneChosen={({ id, display }) => {
          setZoneId(id);
          setZoneName(display);
          getZoneInventoryBalances({ variables: { companyId, zoneId: id } });
        }}
      />
    </Page>
  );
};

export default DestroyUnserialized;
